/**
 * Centralized route -> access rule manifest.
 *
 * Roles (user.user_primary_role, stored as a string) - confirmed:
 *   "1" = Firm Owner
 *   "2" = Recruiter / Resource Manager (RM)
 *   "3" = Resource / Candidate (R)
 *   "4" = Client
 *
 * IMPORTANT — this is a client-side (UX) gate only. It stops a logged-in
 * user from *seeing a page render* when they shouldn't be able to, but it
 * cannot stop someone from calling the underlying API directly. Real
 * enforcement has to happen server-side once the backend verifies identity
 * from a token instead of trusting a posted user_slug (tracked separately -
 * not done yet). Until then, treat every entry here as "hide the door," not
 * "lock the door."
 *
 * Rule shapes:
 *   - null                -> no restriction, open to any authenticated role
 *   - [ROLES.X, ROLES.Y]  -> allowed only for these roles
 *   - (user) => boolean   -> for rules that also depend on something besides
 *                            role (e.g. firm_type) - return true to allow
 *
 * How entries were decided:
 *   - CONFIRMED: mirrors a rule that already existed in code somewhere
 *     (a page's own role check, or Sidebar.jsx's nav show:/comments, which
 *     describe intended access per the person who wrote them) - verified
 *     by hand before adding, source noted per entry.
 *   - UNCONFIRMED / null: no existing signal could be found anywhere in
 *     the codebase for this route. Left open rather than guessed - see
 *     TODO comments.
 */

export const ROLES = {
  FIRM_OWNER: "1",
  RM: "2",
  RESOURCE: "3",
  CLIENT: "4",
};

/**
 * Firm types (user.firm_details.firm_type, stored as a string).
 * Only "1" and "2" have any confirmed usage anywhere in the codebase -
 * the schema also documents a "3" (Independent) but nothing checks it
 * today, so no constant/helper is added for it until it's actually used
 * somewhere (adding one now would be a guess, not a documented fact).
 */
export const FIRM_TYPES = {
  STAFFING: "1",   // Staffing / Consulting
  ENTERPRISE: "2", // Enterprise / End Client
};

/**
 * Semantic role-check helpers. Use these instead of hand-writing
 * `user?.user_primary_role === "2"` in page code - same result, but
 * typo-proof and self-documenting at the call site.
 */
export const isFirmOwner = (user) => user?.user_primary_role === ROLES.FIRM_OWNER;
export const isRM        = (user) => user?.user_primary_role === ROLES.RM;
export const isResource  = (user) => user?.user_primary_role === ROLES.RESOURCE;
export const isClient    = (user) => user?.user_primary_role === ROLES.CLIENT;

/** True if user's role is any of the given roles, e.g. hasAnyRole(user, [ROLES.FIRM_OWNER, ROLES.RM]) */
export const hasAnyRole = (user, roles) => roles.includes(user?.user_primary_role);

/** Semantic firm-type helpers, same pattern as the role helpers above. */
export const isStaffingFirm   = (user) => user?.firm_details?.firm_type === FIRM_TYPES.STAFFING;
export const isEnterpriseFirm = (user) => user?.firm_details?.firm_type === FIRM_TYPES.ENTERPRISE;

// path -> access rule (see shapes above)
export const ROUTE_PERMISSIONS = {
  // ── Dashboard & core ──
  "/dashboard": null, // shown to every role (Sidebar: `show: true`)
  "/account-settings": [ROLES.FIRM_OWNER, ROLES.RM, ROLES.CLIENT], // matches old app's separate "Settings" nav link (roles 1,2,4) - Resource has their own profile via /profile/:id instead
  "/search-v2": null,

  // CONFIRMED via Sidebar.jsx comment: "roles 1,2,4 always; role 3 only if
  // firmType=1" - a plain role array can't express the firmType-conditional
  // part, so this is a function rule.
  "/search": (user) => hasAnyRole(user, [ROLES.FIRM_OWNER, ROLES.RM, ROLES.CLIENT])
    || (isResource(user) && isStaffingFirm(user)),

  // CONFIRMED via Sidebar.jsx comment: "ConnectIQ AI — role 2 only (new feature)"
  "/connectiq": [ROLES.RM],
  "/availability-updates": [ROLES.RM, ROLES.FIRM_OWNER],

  // ── Resources ── CONFIRMED via Sidebar.jsx comment: "My Team / My
  // Candidates — roles 1 and 2 only"
  "/resources": [ROLES.FIRM_OWNER, ROLES.RM],
  "/my-team": [ROLES.FIRM_OWNER, ROLES.RM],

  // Individual profile views - left open. Restricting the *listing* pages
  // above to Owner/RM is confirmed intent, but a specific profile might
  // legitimately be reached via a shared link from another role/flow -
  // no evidence found either way, so not restricting these.
  "/resources/details/:id": null,
  "/my-team/details/:id": null,
  "/profile/:id": null,
  "/edit-profile": null, // safe by construction - EditProfilePage falls back to your own userSlug when no :id is present
  "/resources/edit-resource/:id": null, // ownership now verified INSIDE EditProfilePage itself (canEdit gate added) rather than at the route level, since it depends on the specific target id, not just role
  "/my-team/edit-resource/:id": null, // same as above - ownership check lives in EditProfilePage

  // CONFIRMED via Sidebar.jsx comment: "My Profile — role 2 only + firmType 1"
  // (RMs at staffing firms get their own auto-created resource profile - see
  // Auth.php's check_login_credential, which creates one on first login)
  "/my-resource-profile/details/:id": (user) => isRM(user) && isStaffingFirm(user),

  // ── Jobs ── CONFIRMED via Sidebar.jsx comment: "Job Posting — roles 1, 2,
  // 4 only (NO role 3, NO 'My Jobs' item)". Applied to the listing/management
  // routes; NOTE the backend's create_job itself has no role check at all
  // today (verified in Job.php) - the frontend restriction below is ahead
  // of the backend here, worth aligning them.
  "/jobs": [ROLES.FIRM_OWNER, ROLES.RM, ROLES.CLIENT],
  "/job/latest-jobs": [ROLES.FIRM_OWNER, ROLES.RM, ROLES.CLIENT],
  "/jobs/my-posted-jobs": [ROLES.FIRM_OWNER, ROLES.RM, ROLES.CLIENT], // backend get_my_job_postings itself only allows role 2 or 4 - narrower than this; worth reconciling
  "/job/my-jobs": [ROLES.FIRM_OWNER, ROLES.RM, ROLES.CLIENT],
  "/create-job": [ROLES.FIRM_OWNER, ROLES.RM, ROLES.CLIENT],
  "/create-job-manually": [ROLES.FIRM_OWNER, ROLES.RM, ROLES.CLIENT],
  "/job/edit/:id": [ROLES.FIRM_OWNER, ROLES.RM, ROLES.CLIENT],

  // Individual job detail view - left open, same reasoning as profile
  // detail views above (no evidence a shared job link should be blocked).
  "/jobs/:id": null,
  "/job/details/:id": null,

  // CONFIRMED via Sidebar.jsx comment: "Access Control — role 2 only"
  // (this corrects an earlier guess of Firm-Owner-only, which turned out
  // to be wrong once Sidebar.jsx's own intent was checked)
  "/access-control": [ROLES.RM],

  // ── Resource managers — CONFIRMED Firm-Owner-only, both via Sidebar.jsx
  // comment and the role check these two pages already had internally ──
  "/resource-managers": [ROLES.FIRM_OWNER],
  "/add-resource-manager": [ROLES.FIRM_OWNER],

  // ── Firm — CONFIRMED Firm-Owner-only, same as above ──
  "/edit-firm": [ROLES.FIRM_OWNER],
  "/edit-firm-owner-profile": [ROLES.FIRM_OWNER],
};

/**
 * Matches a real pathname (e.g. "/jobs/abc123") against the manifest's
 * path patterns (e.g. "/jobs/:id"), since routes contain params. Evaluates
 * function rules against the given user; array rules are returned as-is
 * for the caller to check membership against.
 */
export function getRoutePermission(pathname) {
  let rule;
  if (ROUTE_PERMISSIONS.hasOwnProperty(pathname)) {
    rule = ROUTE_PERMISSIONS[pathname];
  } else {
    const segments = pathname.split("/").filter(Boolean);
    for (const pattern of Object.keys(ROUTE_PERMISSIONS)) {
      const patSegments = pattern.split("/").filter(Boolean);
      if (patSegments.length !== segments.length) continue;
      const isMatch = patSegments.every((seg, i) => seg.startsWith(":") || seg === segments[i]);
      if (isMatch) { rule = ROUTE_PERMISSIONS[pattern]; break; }
    }
  }
  // Unknown route not in the manifest at all - fail open (no restriction)
  // rather than fail closed, since AuthWrapper already requires login and
  // an unmapped route is more likely a missed manifest entry than an
  // intentionally sensitive page. Revisit if that assumption doesn't hold.
  return rule === undefined ? null : rule;
}

/** Evaluates a rule (from getRoutePermission) against a user -> true/false */
export function isAllowedByRule(rule, user) {
  if (rule === null || rule === undefined) return true;
  if (typeof rule === "function") return !!rule(user);
  if (Array.isArray(rule)) return rule.includes(user?.user_primary_role);
  return true;
}
